import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../css/header.css";
import NavDropdown from "./NavDropdown";
import menuItems from "../constantes/constDropdown";

const WhatsAppIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    fill="currentColor"
    viewBox="0 0 16 16"
    aria-hidden="true"
  >
    <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .07 3.556.068 7.922c-.002 1.407.367 2.78 1.067 3.99L0 16l4.184-1.096a7.933 7.933 0 0 0 3.808.97h.003c4.365 0 7.922-3.556 7.924-7.922a7.874 7.874 0 0 0-2.318-5.626zM7.995 14.53a6.57 6.57 0 0 1-3.35-.916l-.24-.144-2.484.651.663-2.423-.156-.248a6.54 6.54 0 0 1-1.002-3.49c.002-3.842 3.13-6.97 6.976-6.97a6.93 6.93 0 0 1 4.932 2.044 6.93 6.93 0 0 1 2.039 4.935c-.002 3.84-3.13 6.97-6.978 6.97z" />
    <path d="M11.746 9.852c-.209-.104-1.234-.609-1.425-.678-.191-.07-.33-.104-.468.104-.139.209-.538.678-.66.817-.122.139-.243.156-.452.052-.208-.104-.88-.325-1.676-1.036-.62-.553-1.04-1.235-1.162-1.443-.122-.209-.013-.321.092-.425.094-.094.209-.244.313-.365.104-.122.139-.209.209-.348.07-.139.035-.261-.017-.365-.052-.104-.469-1.13-.643-1.547-.17-.407-.343-.352-.469-.358l-.4-.007c-.14 0-.365.052-.556.261-.191.209-.73.713-.73 1.738s.747 2.017.851 2.156c.104.139 1.47 2.244 3.562 3.145.497.214.885.342 1.188.438.499.159.953.136 1.312.082.4-.06 1.234-.505 1.408-.991.174-.487.174-.904.122-.991-.052-.087-.191-.139-.4-.244z" />
  </svg>
);

const Header = ({
  headerItems: configuredHeaderItems,
  logoUrl,
  companyName,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuToggleRef = useRef(null);
  const menuCloseRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const menuToggle = menuToggleRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    menuCloseRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
        return;
      }

      if (event.key !== "Tab") return;

      const focusableElements = Array.from(
        mobileMenuRef.current?.querySelectorAll(
          "a[href], button:not([disabled])",
        ) || [],
      ).filter((element) => element.getClientRects().length > 0);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      menuToggle?.focus();
    };
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);
  const headerItemOrder = new Map(
    configuredHeaderItems.map((item, index) => [item.id, index]),
  );
  const visibleMenuItems = menuItems
    .filter((item) =>
      configuredHeaderItems.some(
        (configuredItem) =>
          configuredItem.id === item.id && configuredItem.visible,
      ),
    )
    .sort(
      (first, second) =>
        headerItemOrder.get(first.id) - headerItemOrder.get(second.id),
    );

  return (
    <div className="site-header">
      <div className="header-wrapper">
        <Link
          className="site-branding"
          to="/"
          aria-label="Ir para a página inicial"
        >
          <img className="site-logo-image" src={logoUrl} alt="" />
          {companyName && (
            <span className="site-company-name">{companyName}</span>
          )}
        </Link>
        <nav className="site-navigation">
          <div className="site-menu">
            {visibleMenuItems.map((item) => (
              <NavDropdown
                key={item.id}
                title={item.title}
                subItems={item.subItems}
              />
            ))}
          </div>
        </nav>
        <div className="header-right">
          <div className="powered-by">
            <span className="powered-label">powered by</span>
            <span className="powered-name">Gestor Imobiliário 360</span>
          </div>
          <a
            href="https://wa.me/51995513505"
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-btn"
            id="header-whatsapp"
            aria-label="Fale conosco pelo WhatsApp"
          >
            <WhatsAppIcon />
            <span id="header-whatsapp-text">Fale Conosco!</span>
          </a>
        </div>
        <button
          className="mobile-menu-toggle"
          type="button"
          ref={menuToggleRef}
          aria-label="Abrir menu"
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-navigation"
          onClick={() => setIsMobileMenuOpen(true)}
        >
          <span className="mobile-menu-icon" aria-hidden="true">
            <span />
          </span>
        </button>
      </div>

      <div
        className={`mobile-menu-layer${isMobileMenuOpen ? " is-open" : ""}`}
        aria-hidden={!isMobileMenuOpen}
      >
        <button
          className="mobile-menu-backdrop"
          type="button"
          tabIndex={isMobileMenuOpen ? 0 : -1}
          aria-label="Fechar menu"
          onClick={closeMobileMenu}
        />
        <aside
          className="mobile-menu-panel"
          id="mobile-navigation"
          ref={mobileMenuRef}
          role="dialog"
          aria-modal="true"
          aria-label="Menu principal"
        >
          <div className="mobile-menu-header">
            <button
              className="mobile-menu-close"
              type="button"
              ref={menuCloseRef}
              aria-label="Fechar menu"
              tabIndex={isMobileMenuOpen ? 0 : -1}
              onClick={closeMobileMenu}
            >
              <span className="mobile-menu-close-icon" aria-hidden="true" />
            </button>
          </div>
          <nav className="site-navigation" aria-label="Navegação principal">
            <div className="site-menu">
              {visibleMenuItems.map((item) => (
                <NavDropdown
                  key={item.id}
                  title={item.title}
                  subItems={item.subItems}
                  isMobileMenu
                  onNavigate={closeMobileMenu}
                />
              ))}
            </div>
          </nav>
        </aside>
      </div>
    </div>
  );
};
export default Header;
